import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, useToast } from '@src/components/ui';
import { createSalesRep, updateSalesRep } from '../../../api/SalesRepAdminApi';
import styles from './SalesReps.module.scss';
export const getSalesRepErrorMessage = (error) => {
    const payload = error?.response?.body;
    const message = payload?.nonFieldErrors?.[0] ??
        payload?.email?.[0] ??
        payload?.name?.[0] ??
        payload?.reportsTo?.[0] ??
        payload?.phoneNumber?.[0] ??
        payload?.territoryId?.[0] ??
        payload?.detail;
    return typeof message === 'string' && message.trim() ? message : 'Failed to save sales rep';
};
export const SalesRepFormPanel = ({ rep, reps, onClose, onSaved }) => {
    const { showToast } = useToast();
    const isEdit = !!rep;
    const [name, setName] = useState(rep?.name ?? '');
    const [email, setEmail] = useState(rep?.email ?? '');
    const [phone, setPhone] = useState(rep?.phone_number ?? '');
    const [reportsTo, setReportsTo] = useState(rep?.reports_to ?? '');
    const [territory, setTerritory] = useState(rep?.territory_id ?? '');
    const [isActive, setIsActive] = useState(rep?.is_active ?? true);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    // Can't report to yourself or to an inactive rep (keeping an existing inactive manager is allowed).
    const managerOptions = reps.filter((option) => option.id !== rep?.id && (option.is_active || option.id === rep?.reports_to));
    const handleSave = async () => {
        if (!name.trim() || !email.trim()) {
            setErrorMessage('Name and email are required');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        const payload = {
            name: name.trim(),
            email: email.trim(),
            phone_number: phone.trim(),
            reports_to: reportsTo || null,
            territory_id: territory.trim(),
            is_active: isActive,
        };
        try {
            if (rep) {
                await updateSalesRep(rep.id, payload);
            }
            else {
                await createSalesRep(payload);
            }
            showToast({
                message: `Sales rep ${isEdit ? 'updated' : 'created'} successfully`,
                variant: 'success',
                noBorder: true,
            });
            onSaved();
        }
        catch (error) {
            console.error('Error saving sales rep:', error);
            const message = getSalesRepErrorMessage(error);
            setErrorMessage(message);
            showToast({ message, variant: 'error', noBorder: true });
        }
        finally {
            setIsSaving(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: onClose }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: isEdit ? 'Edit Sales Rep' : 'Add Sales Rep' }) }), _jsxs("div", { className: styles.slideOutContent, children: [!!errorMessage && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: errorMessage })), rep && (_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-rep-id", children: "Sales Rep ID" }), _jsx("input", { id: "sales-rep-id", value: rep.sales_rep_id, readOnly: true, disabled: true })] })), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-rep-name", children: "Name" }), _jsx("input", { id: "sales-rep-name", value: name, onChange: (event) => setName(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-rep-email", children: "Email" }), _jsx("input", { id: "sales-rep-email", type: "email", value: email, onChange: (event) => setEmail(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-rep-phone", children: "Phone" }), _jsx("input", { id: "sales-rep-phone", type: "tel", value: phone, onChange: (event) => setPhone(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-rep-reports-to", children: "Reports To" }), _jsxs("select", { id: "sales-rep-reports-to", value: reportsTo, onChange: (event) => setReportsTo(event.target.value), children: [_jsx("option", { value: "", children: "None" }), managerOptions.map((option) => (_jsxs("option", { value: option.id, children: [option.sales_rep_id, " - ", option.name] }, option.id)))] })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "sales-rep-territory", children: "Territory ID (optional)" }), _jsx("input", { id: "sales-rep-territory", value: territory, onChange: (event) => setTerritory(event.target.value) })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: isActive, onChange: (event) => setIsActive(event.target.checked) }), "Is Active"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: onClose }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Sales Rep', onClick: handleSave, disabled: isSaving })] })] })] }));
};
export default SalesRepFormPanel;
